import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { formatNGN } from "@/lib/api";
import PaymentBadges from "@/components/PaymentBadges";

const COMPARE_ROWS = [
  { label: "Power draw", bad: "1,500–2,400W", good: "500W" },
  { label: "On a 1.5kVA inverter", bad: "Overloads or trips it", good: "Runs comfortably" },
  { label: "Battery drain", bad: "Heavy — flattens your bank fast", good: "Roughly half the load or less" },
  { label: "Works during power cuts", bad: "Rarely — grid only", good: "Yes — solar & inverter ready" },
  { label: "Soleplate", bad: "Bare aluminium, sticks to fabric", good: "Ceramic non-stick glide" },
  { label: "Generator fuel cost", bad: "High — big gen required", good: "Low — small gen friendly" },
  { label: "Warranty", bad: "Often none", good: "12 months, Nigeria-wide" },
];

const WattageCards = () => (
  <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-8">
    <div className="p-8 sm:p-10 md:p-14 glossy rounded-2xl">
      <div className="eyebrow">Ordinary dry iron</div>
      <div className="font-serif mt-4 font-mono" style={{ fontSize: "clamp(2.4rem, 7vw, 5rem)", lineHeight: 1, color: "var(--ink)" }}>
        1,500<span style={{ color: "var(--ink-muted)" }}>–</span>2,400<span className="text-2xl ml-2">W</span>
      </div>
      <p className="mt-6 max-w-md" style={{ color: "var(--ink-soft)" }}>
        Typical household model. Spikes your inverter load and burns through battery — fast.
      </p>
      <div className="mt-10 h-2 rounded-full overflow-hidden" style={{ background: "var(--border)" }}>
        <div style={{ width: "100%", height: "100%", background: "var(--ink-soft)" }} />
      </div>
    </div>

    <div className="p-8 sm:p-10 md:p-14 glossy-deep rounded-2xl relative overflow-hidden">
      <div className="sunset-ambient" style={{ top: "-200px", right: "-200px", width: "400px", height: "400px", opacity: 0.55 }} />
      <div className="relative z-10">
        <div className="eyebrow">SunPress CORE 500</div>
        <div className="font-serif mt-4 sunset-text font-mono" style={{ fontSize: "clamp(2.4rem, 7vw, 5rem)", lineHeight: 1 }}>
          500<span className="text-2xl ml-2">W</span>
        </div>
        <p className="mt-6 max-w-md" style={{ color: "var(--ink-soft)" }}>
          Low-watt, inverter-ready. Engineered to do the same job on roughly a quarter of the power.
        </p>
        <div className="mt-10 h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
          <div className="sunset-bg" style={{ width: "26%", height: "100%" }} />
        </div>
      </div>
    </div>
  </div>
);

const CompareRow = ({ r }) => (
  <tr>
    <th scope="row">{r.label}</th>
    <td>
      <span className="inline-flex items-start gap-2">
        <X size={15} strokeWidth={2.4} className="mt-0.5 flex-shrink-0" style={{ color: "var(--ink-muted)" }} />
        {r.bad}
      </span>
    </td>
    <td className="compare-core">
      <span className="inline-flex items-start gap-2 font-medium" style={{ color: "var(--ink)" }}>
        <Check size={15} strokeWidth={2.6} className="mt-0.5 flex-shrink-0" style={{ color: "var(--sun-orange)" }} />
        {r.good}
      </span>
    </td>
  </tr>
);

const CompareTable = () => (
  <div className="mt-8 glossy rounded-2xl overflow-hidden">
    <div className="overflow-x-auto">
      <table className="compare-table" data-testid="compare-table">
        <thead>
          <tr>
            <th scope="col" aria-label="Feature" />
            <th scope="col">Ordinary dry iron</th>
            <th scope="col" className="compare-core">
              <span className="sunset-text font-serif" style={{ fontSize: "1.05rem", letterSpacing: "-0.01em" }}>SunPress CORE 500</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {COMPARE_ROWS.map((r) => <CompareRow key={r.label} r={r} />)}
        </tbody>
      </table>
    </div>
  </div>
);

export default function ComparisonSection({ core }) {
  return (
    <section id="problem" className="py-20 md:py-32">
      <WattageCards />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-14 md:mt-20">
        <div className="eyebrow">Side by side</div>
        <h2 className="display mt-3" style={{ fontSize: "clamp(1.9rem, 4vw, 3.2rem)", color: "var(--ink)" }}>
          Same crisp press. <span className="sunset-text">A quarter of the fight.</span>
        </h2>
        <CompareTable />
        <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
          <Link to="/products/CORE-500" data-testid="compare-shop-cta" className="btn-brand justify-center w-full sm:w-auto">
            Make the switch {core && <>— {formatNGN(core.price_ngn)}</>} <ArrowRight size={16} />
          </Link>
          <PaymentBadges />
        </div>
      </div>
    </section>
  );
}
